/**
 * What a faction thinks of you.
 *
 * One score per faction, moved by what you do and read by everything that
 * decides how you are treated. The rules are in `Interspacial.md` under
 * "faction ranks"; this is that section, in code.
 *
 * ── Why a mission moves three numbers ───────────────────────────────────────
 *
 * Because factions are not indifferent to each other. Carrying for one is a
 * small favour to their ally and a small slight to the people they dislike, so
 * a player who only ever works one contract list drifts into a position rather
 * than choosing one. That is the whole point of the spread: standing is a shape
 * across five factions, not five independent bars.
 */
import { FACTIONS, FACTION_IDS } from '../factions.js';
import { SELL_SCARCE } from './selling.js';
import { RESTOCK } from '../world/clock.js';
/**
 * The floor and ceiling.
 *
 * Below zero a faction is hostile and will not have you on their pads; below
 * −5 they shoot on sight. The floor sits a little under that so "irredeemable"
 * is a place you can actually reach and sit at, rather than a wall you bounce
 * off the moment you cross into hostility.
 */
export const STANDING_MIN = -10;
export const STANDING_MAX = 100;
/** Where everybody starts: known of, trusted with nothing. */
export const STANDING_START = 10;
/**
 * Factions that keep a score at all.
 *
 * Turquoise is always neutral — it takes no side and therefore holds no opinion,
 * so it has no rank. Derived from `alwaysNeutral` rather than listing an id,
 * because the reason it has no score is a property of the faction.
 */
export const RANKED_FACTION_IDS = FACTION_IDS.filter((id) => !FACTIONS[id].alwaysNeutral && !FACTIONS[id].alwaysHostile);
/** Whether this faction keeps a score. */
export function isRanked(id) {
    return RANKED_FACTION_IDS.includes(id);
}
/**
 * Held to the range, and left as it is otherwise.
 *
 * Deliberately does not round. Standing is continuous — an ally's share of a
 * short run is six tenths of a point — and rounding at every step would turn
 * every small change into either nothing or a whole point. `shownStanding` is
 * where it becomes a number a player reads.
 */
export function clampStanding(value) {
    return Math.min(STANDING_MAX, Math.max(STANDING_MIN, value));
}
/**
 * Standing as a player sees it: whole numbers.
 *
 * Rounded once, at the end, rather than at every step. Somebody sitting on 24.7
 * reads as 25 and is not yet allied, which is the correct way round — the bands
 * are decided on the true value, and this only decides what is printed.
 */
export function shownStanding(value) {
    return Math.round(value);
}
/**
 * How much a completed run is worth to the faction that wanted it.
 *
 * Scaled by distance, like everything else about a mission. Deliberately not
 * large: from a start of ten it takes about fifteen long hauls to be honoured,
 * which is a relationship rather than a number that moves in an afternoon.
 */
export const MISSION_STANDING = {
    close: 1,
    far: 3,
    distant: 6,
};
/** What an ally gains when you do a faction a favour. */
export const ALLY_SHARE = 0.6;
/** What somebody they dislike loses by it. */
export const ENEMY_SHARE = -0.3;
/**
 * Every standing change one action causes.
 *
 * Returns the whole spread — the faction itself, their allies, the people they
 * dislike — so a caller applies one map rather than remembering the rule.
 *
 * Fractions are kept. A short run is six tenths of a point to an ally and three
 * tenths off a rival; those are real amounts that accumulate across runs, and
 * rounding them here would make the cheapest missions either free of
 * consequence or worth a whole point. Standing is stored continuous and rounded
 * once, for display — see `shownStanding`.
 */
export function standingChanges(faction, amount) {
    const changes = {};
    if (!isRanked(faction) || amount === 0)
        return changes;
    changes[faction] = amount;
    for (const ally of FACTIONS[faction].allies) {
        if (isRanked(ally))
            changes[ally] = (changes[ally] ?? 0) + amount * ALLY_SHARE;
    }
    for (const enemy of FACTIONS[faction].dislikes) {
        if (isRanked(enemy))
            changes[enemy] = (changes[enemy] ?? 0) + amount * ENEMY_SHARE;
    }
    return changes;
}
export function rankOf(standing) {
    if (standing < -5)
        return 'attacked';
    if (standing < 0)
        return 'hostile';
    if (standing >= 75)
        return 'honored';
    if (standing >= 50)
        return 'trusted';
    if (standing >= 25)
        return 'allied';
    return 'neutral';
}
/** Whether a faction will let you put down on their pads. */
export function willBerth(standing) {
    return standing >= 0;
}
/** Whether they shoot first. */
export function shootsOnSight(standing) {
    return standing < -5;
}
/**
 * Distrusted: liked by their rivals, and not by them.
 *
 * A separate question from your own standing with them, because it is not about
 * what you did to *them* — it is about the company you keep. Honoured by
 * somebody they dislike, without being allied to them, closes their pads
 * without making them open fire.
 */
export function isDistrusted(faction, standings) {
    const own = standings[faction] ?? STANDING_START;
    if (own >= 25)
        return false;
    return FACTIONS[faction].dislikes.some((rival) => (standings[rival] ?? STANDING_START) > 75);
}
// ── What standing is worth ─────────────────────────────────────────────────
/**
 * The most a discount may ever be.
 *
 * **This is a free-money bound, not a balance choice.** Anything bought can be
 * sold back, and the best rate anything sells back at is `SELL_SCARCE`. If a
 * discount ever took the buy price below what a sale returns, the loop is:
 * buy at the discount, sell to a station that has none in, keep the difference,
 * repeat. Not a slow exploit — an infinite one, at whatever rate the player can
 * click.
 *
 * So the ceiling is one minus the *highest* sell rate rather than the ordinary
 * one. Using the in-stock rate would leave exactly this hole open for anything
 * scarce, which is the case a player would find first, because scarce goods are
 * the ones worth carrying anywhere.
 */
export const MAX_DISCOUNT = 1 - SELL_SCARCE;
/**
 * What a faction knocks off, as a fraction of the price.
 *
 * Cumulative with rank, and clamped to `MAX_DISCOUNT` at the end — so tuning
 * these upward can make the top ranks stop improving, but it cannot open the
 * buy-low-sell-high loop.
 */
export function factionDiscount(standing) {
    const rank = rankOf(standing);
    const offered = rank === 'honored' ? 0.2 : rank === 'trusted' ? 0.12 : rank === 'allied' ? 0.06 : 0;
    return Math.min(MAX_DISCOUNT, offered);
}
/** A price, after whatever this faction thinks of you. */
export function discounted(price, standing) {
    return price * (1 - factionDiscount(standing));
}
/**
 * How often an honoured faction will put your hull back together for nothing.
 *
 * **Global, not per faction.** Being honoured by three factions is a reason to
 * be pleased with yourself, not three free repairs a window — the cooldown is
 * on the player, and any faction that thinks well enough of you can serve it.
 *
 * One restock window, so it lands on the same rhythm as shelves and mission
 * boards. A player who learns "the station refreshes every two hours" has
 * learned this too.
 */
export const FREE_REPAIR_COOLDOWN_SECONDS = RESTOCK;
/** Whether this faction would do it, standing alone. */
export function offersFreeRepair(standing) {
    return rankOf(standing) === 'honored';
}
/**
 * Whether the free repair is off cooldown.
 *
 * `last` is when it was last taken, in epoch milliseconds, or null for never.
 */
export function freeRepairReady(last, now = Date.now()) {
    if (last === null)
        return true;
    return now - last >= FREE_REPAIR_COOLDOWN_SECONDS * 1000;
}
/** Seconds until it comes back, or zero if it is ready. */
export function freeRepairIn(last, now = Date.now()) {
    if (last === null)
        return 0;
    const left = FREE_REPAIR_COOLDOWN_SECONDS * 1000 - (now - last);
    return Math.max(0, Math.ceil(left / 1000));
}
//# sourceMappingURL=standing.js.map